import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import NonAuthLayout from "../../Layout/NonAuthLayout";
import { ArrowLeft, AlertCircle, CheckCircle, XCircle } from "lucide-react";
import { GetSingleCartCollactionThunk } from "../../redux/states/cartCollection/thunk";

const ViewAllCartData = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { uuid } = useParams();
  const { singlecart, loading } = useSelector((state) => state.cart);
  const API_URL = process.env.REACT_APP_API_URL;

  useEffect(() => {
    dispatch(GetSingleCartCollactionThunk(uuid));
  }, [dispatch, uuid]);

  // ⏳ Loading
  if (loading) {
    return (
      <NonAuthLayout>
        <div className="min-h-screen flex items-center justify-center">
          <p className="text-lg font-semibold">Loading cart details...</p>
        </div>
      </NonAuthLayout>
    );
  }

  // ❌ Not Found
  if (!singlecart) {
    return (
      <NonAuthLayout>
        <div className="min-h-screen flex flex-col items-center justify-center">
          <XCircle className="w-16 h-16 text-red-500 mb-4" />
          <h2 className="text-xl font-bold mb-2">Cart Item Not Found</h2>

          <button
            onClick={() => navigate(-1)}
            className="bg-blue-600 text-white px-6 py-2 rounded-xl"
          >
            Back
          </button>
        </div>
      </NonAuthLayout>
    );
  }

  // 🌟 Extract Data
  const item = singlecart;
  const user = item.cart_user;
  const product = item.product_data;

  const getStatusBadge = (status) => {
    switch (status) {
      case "active":
        return { color: "bg-green-100 text-green-800", icon: CheckCircle };
      case "pending":
        return { color: "bg-yellow-100 text-yellow-800", icon: AlertCircle };
      default:
        return { color: "bg-gray-100 text-gray-800", icon: AlertCircle };
    }
  };

  const statusBadge = getStatusBadge(item.status);
  const StatusIcon = statusBadge.icon;

  return (
    <NonAuthLayout>
      <div className="min-h-screen p-3">
        {/* Header */}
        <div className="flex items-center justify-between bg-white p-3 rounded-xl shadow border">
          <button
            onClick={() => navigate(-1)}
            className="flex gap-2 items-center"
          >
            <ArrowLeft /> Back
          </button>

          <h3 className="font-bold text-gray-800">Cart Item Details</h3>

          <span
            className={`px-3 py-1 rounded-full flex items-center gap-1 ${statusBadge.color}`}
          >
            <StatusIcon className="w-4 h-4" />
            {item.status}
          </span>
        </div>

        {/* Content */}
        <div className="bg-white rounded-xl shadow border p-4 mt-3 space-y-6">
          {/* Cart Info */}
          <Section title="Cart Information">
            <InfoRow label="UUID" value={item.uuid} />
            <InfoRow label="Order Status" value={item.order_status} />
            <InfoRow label="Quantity" value={item.quantity} />
            <InfoRow label="Group ID" value={item.group_id} />
            <InfoRow
              label="Created"
              value={new Date(item.created_at).toLocaleString("en-IN")}
            />
          </Section>

          {/* User Info */}
          <Section title="User Information">
            <InfoRow label="Name" value={user?.name} />
            <InfoRow label="Email" value={user?.email} />
            <InfoRow label="Mobile" value={user?.mobile} />
            <InfoRow label="City" value={user?.city} />
            <InfoRow label="State" value={user?.state} />
            <InfoRow label="Country" value={user?.country} />
          </Section>

          {/* Product Info */}
          <Section title="Product Information">
            <InfoRow label="Name" value={product?.name} />
            <InfoRow label="SKU" value={product?.sku} />
            <InfoRow label="Brand" value={product?.brand_name} />
            <InfoRow label="MRP" value={`₹${product?.mrp}`} />
            <InfoRow
              label="Wholesale Price"
              value={`₹${product?.wholesale_price}`}
            />
            <InfoRow label="Coupon" value={product?.coupon_code} />
          </Section>

          {/* Product Image */}
          <div>
            <h3 className="font-semibold mb-2">Product Image</h3>
            <img
              src={`${API_URL}/${product?.image}`}
              className="h-60 rounded-xl border"
              alt="product"
            />
          </div>
        </div>
      </div>
    </NonAuthLayout>
  );
};

// ---------- Helper Components ----------
const Section = ({ title, children }) => (
  <div>
    <h3 className="text-md font-semibold text-gray-800 mb-2">{title}</h3>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">{children}</div>
  </div>
);

const InfoRow = ({ label, value }) => (
  <div className="flex justify-between bg-gray-50 px-4 py-2 rounded-lg border">
    <span className="font-medium text-gray-700">{label}</span>
    <span className="text-gray-900">{value || "—"}</span>
  </div>
);

export default ViewAllCartData;
